import { Box, Group, Indicator, Kbd, Loader, Stack, Text, Tooltip } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { i18nKeys, useTranslation } from 'locales';
import { isEmpty } from 'remeda';
import type { DescVal } from 'types/storeTypes';
import { useMissingRecommendedVariables } from './use-missing-recommended-variables';
import { useSaveTemplate } from './use-save-template';

export const TemplateEditorStatusBar = () => {
  const { t } = useTranslation();
  const { hasUnsavedChanges, isSaving } = useSaveTemplate();
  const missingVariables = useMissingRecommendedVariables();

  return (
    <Box
      h="100%"
      px="lg"
      bg="gray.0"
      style={{
        borderTop: `1px solid var(--mantine-color-gray-3)`,
        // Jodit's workplace is position:relative, so it paints above static
        // siblings and swallowed this border across the editor column.
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'center',
      }}
    >
      <Box style={{ justifySelf: 'start' }}>
        <SaveStatusDisplay isSaving={isSaving} hasUnsavedChanges={hasUnsavedChanges} />
      </Box>
      <Group gap={6} justify="center">
        <Kbd>/</Kbd>
        <Text size="xs" c="gray">
          {t(i18nKeys.TEMPLATE.INSERT_VARIABLE_HINT)}
        </Text>
      </Group>
      <Box style={{ justifySelf: 'end' }}>
        <MissingVariablesWarning missingVariables={missingVariables} />
      </Box>
    </Box>
  );
};

const MissingVariablesWarning = ({ missingVariables }: { missingVariables: DescVal[] }) => {
  const { t } = useTranslation();

  if (isEmpty(missingVariables)) return <span />;

  return (
    <Tooltip
      multiline
      label={
        <Stack gap={2}>
          {missingVariables.map(variable => (
            <Text key={variable.value} size="xs">
              {variable.description}
            </Text>
          ))}
        </Stack>
      }
    >
      <Group gap={6} c="orange.9" style={{ cursor: 'default' }}>
        <IconAlertTriangle size={14} stroke={1.5} />
        <Text size="xs" c="orange.9">
          {missingVariables.length === 1
            ? t(i18nKeys.TEMPLATE.RECOMMENDED_VARIABLES_UNUSED_ONE)
            : t(i18nKeys.TEMPLATE.RECOMMENDED_VARIABLES_UNUSED_OTHER, {
                count: missingVariables.length,
              })}
        </Text>
      </Group>
    </Tooltip>
  );
};

const SaveStatusDisplay = ({
  isSaving,
  hasUnsavedChanges,
}: {
  isSaving: boolean;
  hasUnsavedChanges: boolean;
}) => {
  const { t } = useTranslation();

  if (isSaving) {
    return (
      <Group gap={8}>
        <Loader size={12} color="gray.6" />
        <Text size="xs" c="gray.7" fw={500}>
          {t(i18nKeys.TEMPLATE.SAVE_STATUS.SAVING)}
        </Text>
      </Group>
    );
  }

  const isSaved = !hasUnsavedChanges;

  return (
    <Indicator
      position="middle-start"
      offset={-10}
      ml={10}
      size={8}
      color={isSaved ? 'green.4' : 'orange.4'}
    >
      <Text size="xs" c={isSaved ? 'green.9' : 'orange.9'} fw={500}>
        {t(isSaved ? i18nKeys.TEMPLATE.SAVE_STATUS.SAVED : i18nKeys.TEMPLATE.SAVE_STATUS.UNSAVED)}
      </Text>
    </Indicator>
  );
};
